/**
 * MediaTicket — Media (photos, video, flight paths) ticket content for EditorPanel.
 * Extracted from EditorPanel.tsx (refactoring, no behavior changes).
 */

import { useRef } from 'react';
import { useProjectStore, useUIStore, useMapStore } from '../../../stores';
import { useIsReadOnlyViewer } from '../../../hooks/useShareViewer';
import { Button } from '../../common';
import { processGeoPhotos, parseSrtFile, uniqueRelPhotoPath, storePhotoBlob, rectGeomFromCenter, defaultSizeForType, rotatePolygonAroundCenter } from '../../../utils';
import { matchLocalPhotoSets, flightBaseName, videoRole } from '../../../utils/driveImport';

export default function MediaTicket() {
  // F-02E-2B: every control in this ticket is upload/import/register —
  // pure owner surface. Share viewers get the geo-photo count and
  // nothing else.
  const isReadOnly = useIsReadOnlyViewer();
  const indications = useProjectStore((s) => s.data.indications);
  const openModal = useUIStore((s) => s.openModal);
  const openPanel = useUIStore((s) => s.openPanel);
  const addNotification = useUIStore((s) => s.addNotification);

  const batchMoveActive = useMapStore((s) => s.batchMoveActive);
  const batchMovePhotoCount = useMapStore((s) => s.batchMovePhotoCount);

  const geoPhotos = useProjectStore((s) => s.data.geoPhotos);

  // File input refs
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const flightPathInputRef = useRef<HTMLInputElement>(null);

  // ============================================
  // HANDLERS
  // ============================================
  const handleMobileUploads = () => {
    openPanel('mobileUploadsPanel');
  };

  const handleUploadPhotos = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const fileCount = files.length;
    addNotification({ type: 'info', title: 'Processing photos...', message: `${fileCount} file${fileCount > 1 ? 's' : ''}` });
    try {
      const results = await processGeoPhotos(files);
      console.log(`[GeoPhotos] ${fileCount} files selected, ${results.length} recognized as images`);

      let addedCount = 0, noGpsCount = 0, pairedCount = 0;
      const orientationDeg = useUIStore.getState().defaultIndicationOrientation || 0;

      // Get map center as fallback for photos without GPS
      const mapCenter = useMapStore.getState().map?.getCenter();
      const fallbackLng = mapCenter?.lng ?? 34.78;
      const fallbackLat = mapCenter?.lat ?? 32.07;

      // Pair thermal/visual/zoom photos sharing a base name into a single marker
      // — same behavior as the Google Drive import (F-04A). The anchor (thermal,
      // else visual, else zoom) supplies the primary image + GPS/EXIF/geometry;
      // the remaining variants ride along as visualImage/zoomImage. Visual-only,
      // zoom-only, RGB-only and no-GPS sets still produce a marker (no skipping).
      const items = results.map((r) => ({ name: r.file.name, file: r.file, data: r.data }));
      const photoSets = matchLocalPhotoSets(items);

      for (const set of photoSets) {
        const anchor = set.thermal ?? set.visual ?? set.zoom;
        if (!anchor) continue;
        const file = anchor.file;
        const geoData = anchor.data;

        // Use GPS coordinates if available, otherwise place at map center
        let lng: number, lat: number;
        const hasGps = !!geoData.coordinates;
        if (hasGps) {
          [lng, lat] = geoData.coordinates!;
        } else {
          noGpsCount++;
          lng = fallbackLng + (Math.random() - 0.5) * 0.0002;
          lat = fallbackLat + (Math.random() - 0.5) * 0.0002;
        }

        // Store anchor blob for local preview (primary image)
        const relPath = uniqueRelPhotoPath(file.name);
        storePhotoBlob(relPath, file);

        // Use blob path for instant local display (uploaded on explicit "Save to Server")
        const imagePath = relPath;

        // Store paired visual/zoom siblings as local blobs. Skip a slot that IS
        // the anchor (thermal missing → visual/zoom promoted to primary) so we
        // never double-store the same file or render a redundant toggle.
        let visualImage: string | undefined;
        let zoomImage: string | undefined;
        if (set.visual && set.visual !== anchor) {
          const relV = uniqueRelPhotoPath(set.visual.file.name);
          storePhotoBlob(relV, set.visual.file);
          visualImage = relV;
        }
        if (set.zoom && set.zoom !== anchor) {
          const relZ = uniqueRelPhotoPath(set.zoom.file.name);
          storePhotoBlob(relZ, set.zoom.file);
          zoomImage = relZ;
        }
        if (visualImage || zoomImage) pairedCount++;

        // Build rectangle geometry
        const sz = defaultSizeForType('Photo');
        let geom = rectGeomFromCenter(lng, lat, sz.w, sz.h);

        if (orientationDeg) {
          geom = rotatePolygonAroundCenter(geom, [lng, lat], orientationDeg);
        }

        // Build notes
        const notesLines: string[] = [];
        notesLines.push(`Photo file: ${file.name}`);
        if (visualImage) notesLines.push(`Visual: ${set.visual!.file.name}`);
        if (zoomImage) notesLines.push(`Zoom: ${set.zoom!.file.name}`);
        if (geoData.captureDate) notesLines.push(`Captured: ${geoData.captureDate}`);
        if (hasGps) {
          notesLines.push(`GPS: ${lat.toFixed(7)}, ${lng.toFixed(7)}`);
        } else {
          notesLines.push('GPS: not available (placed at map center)');
        }
        if (geoData.relativeAltitude != null) notesLines.push(`Flight height: ${geoData.relativeAltitude.toFixed(1)}m`);
        if (geoData.altitude != null) notesLines.push(`Altitude ASL: ${geoData.altitude.toFixed(1)}m`);
        if (geoData.accuracy != null) notesLines.push(`Accuracy: ${geoData.accuracy.toFixed(1)}m`);

        const now = new Date().toISOString();
        const captureDate = useProjectStore.getState().selectedDate
          || geoData.captureDate
          || now.split('T')[0];

        useProjectStore.getState().addIndication({
          id: `ind_photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'Photo',
          geometry: geom,
          properties: {
            image: imagePath,
            // Promoted visual/zoom anchor (no thermal in the set): flag the
            // primary image so viewers show it in the visual slot.
            imageRole: anchor === set.thermal ? undefined : ('visual' as const),
            visualImage,
            zoomImage,
            notes: notesLines.join('\n'),
            rotationDeg: orientationDeg,
            captureDate,
            createdAt: geoData.capturedAt || now,
            updatedAt: now,
            // EXIF metadata for photo registration
            bearing: geoData.bearing,
            relativeAltitude: geoData.relativeAltitude,
            altitude: geoData.altitude,
            gimbalPitch: geoData.gimbalPitch,
            gimbalYaw: geoData.gimbalYaw,
            gimbalRoll: geoData.gimbalRoll,
            flightYaw: geoData.flightYaw,
            exifData: geoData.exifData,
          },
        });
        addedCount++;
      }

      console.log(`[GeoPhotos] Added ${addedCount} marker(s) from ${results.length} image(s); paired: ${pairedCount}, no GPS: ${noGpsCount}`);

      if (addedCount > 0) {
        useProjectStore.getState().updateAvailableDates();
        const parts: string[] = [];
        if (pairedCount > 0) parts.push(`${pairedCount} paired thermal/visual`);
        if (noGpsCount > 0) parts.push(`${noGpsCount} placed at map center (no GPS)`);
        const msg = parts.length > 0 ? parts.join(' · ') : undefined;
        addNotification({ type: 'success', title: `Added ${addedCount} photo marker${addedCount > 1 ? 's' : ''}`, message: msg });
      } else if (results.length === 0) {
        addNotification({ type: 'warning', title: 'No images found', message: `${fileCount} file(s) were not recognized as images` });
      }
    } catch (error) {
      console.error('[GeoPhotos] Upload error:', error);
      addNotification({ type: 'error', title: 'Failed to process photos', message: error instanceof Error ? error.message : undefined });
    }
    e.target.value = '';
  };

  const handleUploadVideo = () => {
    videoInputRef.current?.click();
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const srtFiles: File[] = [];
    const videoFiles: File[] = [];
    for (const file of Array.from(files)) {
      if (file.name.toLowerCase().endsWith('.srt')) srtFiles.push(file);
      else videoFiles.push(file);
    }
    if (srtFiles.length > 0) processFlightPathFiles(srtFiles);
    if (videoFiles.length > 0) {
      addNotification({ type: 'info', title: `${videoFiles.length} video files selected`, message: 'Video preview coming soon' });
    }
    e.target.value = '';
  };

  const handleFlightPathSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    processFlightPathFiles(Array.from(files));
    e.target.value = '';
  };

  const processFlightPathFiles = async (files: File[]) => {
    // Group SRTs sharing a flight base name into ONE flight path (a DJI
    // _T/_W/_Z/_S set produces one SRT per role). Parse the primary SRT for
    // coordinates in role preference thermal → visual → zoom → screen. Video
    // files themselves are not uploaded here — the owner links per-role video
    // URLs later via the flight path's Edit panel.
    const ROLE_PREF: Record<string, number> = { thermal: 0, visual: 1, zoom: 2, screen: 3 };
    const srtFiles = files.filter((f) => f.name.toLowerCase().endsWith('.srt'));
    const groups = new Map<string, File[]>();
    for (const f of srtFiles) {
      const base = flightBaseName(f.name);
      const arr = groups.get(base) ?? [];
      arr.push(f);
      groups.set(base, arr);
    }

    let addedCount = 0;
    for (const [base, groupFiles] of groups) {
      // Pick the coordinate SRT by role preference (stable: earliest selected wins ties).
      const primary = [...groupFiles].sort(
        (a, b) => ROLE_PREF[videoRole(a.name)] - ROLE_PREF[videoRole(b.name)],
      )[0];
      try {
        const flightData = await parseSrtFile(primary);
        if (flightData.coordinates.length > 0) {
          const color = '#ff8c00';
          const fpCaptureDate = useProjectStore.getState().selectedDate || undefined;
          useProjectStore.getState().addFlightPath({
            id: `fp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: base || flightData.name,
            coordinates: flightData.coordinates,
            timestamps: flightData.timestamps, visible: true, color, captureDate: fpCaptureDate,
          });
          if (fpCaptureDate) useProjectStore.getState().updateAvailableDates();
          addedCount++;
        }
      } catch (error) {
        console.error('SRT parse error:', error);
      }
    }
    if (addedCount > 0) {
      addNotification({ type: 'success', title: `Added ${addedCount} flight path(s)` });
    } else {
      addNotification({ type: 'warning', title: 'No flight paths added', message: 'No valid GPS data found' });
    }
  };

  // ============================================
  // PHOTO REGISTRATION
  // ============================================

  return (
    <>
      {/* Hidden file inputs for media uploads */}
      <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect} style={{ display: 'none' }} />
      <input ref={videoInputRef} type="file" accept="video/*,.srt" multiple onChange={handleVideoSelect} style={{ display: 'none' }} />
      <input ref={flightPathInputRef} type="file" accept=".srt,.csv,.gpx" onChange={handleFlightPathSelect} style={{ display: 'none' }} />

      <div className="ep-ticket-content" data-testid="media-ticket">
        {isReadOnly ? (
          <>
            <div className="ep-section-label">Media</div>
            {(indications.filter((i) => i.type === 'Photo').length > 0 || geoPhotos.length > 0) ? (
              <p className="ep-status-text" data-testid="media-ticket-readonly-count">
                {indications.filter((i) => i.type === 'Photo').length + geoPhotos.length} geo photos
              </p>
            ) : (
              <p className="ep-empty-text">No media available in this shared view.</p>
            )}
          </>
        ) : (
          <>
            <div className="ep-section-label">Upload</div>
            <Button variant="secondary" size="sm" fullWidth onClick={handleUploadPhotos}>
              Upload Geo Photos
            </Button>
            <Button variant="secondary" size="sm" fullWidth onClick={handleUploadVideo}>
              Upload Geo Video
            </Button>
            <Button variant="secondary" size="sm" fullWidth onClick={() => openModal('driveImport')}>
              Import from Google Drive
            </Button>
            <Button
              variant={batchMoveActive ? 'warning' : 'secondary'}
              size="sm"
              fullWidth
              onClick={() => {
                if (batchMoveActive) {
                  (useMapStore.getState() as any)._batchMoveFinish?.();
                } else {
                  (useMapStore.getState() as any)._batchMoveStart?.();
                }
              }}
            >
              {batchMoveActive
                ? `BATCH MOVE: ${batchMovePhotoCount} photos (Enter/Esc)`
                : 'Batch Move Photos'}
            </Button>
            <Button variant="primary" size="sm" fullWidth onClick={handleMobileUploads}>
              Mobile Uploads
            </Button>

            <div className="ep-divider" />
            <div className="ep-section-label">Photo Registration</div>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => openPanel('photoRegPanel')}
              disabled={indications.filter((i) => i.type === 'Photo').length === 0 && geoPhotos.length === 0}
            >
              Register Photos
            </Button>
            {(indications.filter((i) => i.type === 'Photo').length > 0 || geoPhotos.length > 0) && (
              <p className="ep-status-text">
                {indications.filter((i) => i.type === 'Photo').length + geoPhotos.length} geo photos
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
